import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { FaArrowUp, FaLaptopMedical, FaPlusSquare } from "react-icons/fa";
import DashboardContext from "../context/DashboardContext";
import MembersContext from "../context/MembersContext";
import ActiveMembersCard from "../components/members/ActiveMembersCard";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import EditIcon from "@mui/icons-material/Edit";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Container from "@mui/material/Container";
import CreateIcon from "@mui/icons-material/Create";
import CustomButton from "../components/CustomButton";
import CustomContainer from "../components/CustomContainer";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Drawer from "@mui/material/Drawer";
import FirstPageRoundedIcon from "@mui/icons-material/FirstPageRounded";
import IconButton from "@mui/material/IconButton";
import InactiveMembersCard from "../components/members/InactiveMembersCard";
import MembersFilter from "../components/members/MembersFilter";
import MemberGeneratedEconomyCard from "../components/members/MemberGeneratedEconomyCard";
import MemberNumberOfUsesCard from "../components/members/MemberNumberOfUsesCard";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import {
  CircularProgress,
  Switch,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
  Grid,
} from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  fetchAllMembers,
  activeInativeMembers,
  fetchTransactionsHistory,
  setNewMember,
  inativeAssociated,
} from "../services";
import { dateTimeFormatter } from "../utils";
import ModalTelemedicine from "../components/members/ModalTelemedicine";

export default function Members() {
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [filterTypeInput, setFilterTypeInput] = useState("cpf");
  const [cpfInput, setCpfInput] = useState("");
  const [nameInput, setNameInput] = useState("");
  const [addressInput, setAddressInput] = useState("");
  const [addressNumInput, setAddressNumInput] = useState("");
  const [productType, setProductType] = useState("");
  const [isMemberActivationDialogOpen, setIsMemberActivationDialogOpen] = useState(false);
  const [memberDetails, setMemberDetails] = useState("");
  const [membersPage, setMembersPage] = useState(0);
  const [memberToBeActivatedCpf, setMemberToBeActivatedCpf] = useState("");
  const [isOnlyActiveMembers, setIsOnlyActiveMembers] = useState(true);
  const [associatedAction, setAssociatedAction] = useState("");
  const [orderBy, setOrderBy] = useState(localStorage.getItem("orderBy") || "");
  const [initialDate, setInitialDate] = useState("");
  const [finalDate, setFinalDate] = useState("");
  const [selectedUserCpf, setSelecteduserCpf] = useState(null)
  const [openModal, setOpenModal] = useState(null);

  const closeModal = () => setOpenModal(null);

  // Novo estado para armazenar os filtros quando o botão "Filtrar" for clicado
  const [filters, setFilters] = useState({});

  const {
    drawerWidth,
    isMemberDrawerOpen,
    setIsMemberDrawerOpen,
    nodeToken,
    setEditMember,
    products
  } = useContext(DashboardContext);
  const hasTelemedicine = products?.some((el) => Number(el.id) === 4 || Number(el.id) === 14)

  const navigation = useNavigate();
  const theme = useTheme(); // Obtém o tema

  // Função de fetch para o React Query
  const fetchMembers = async ({ queryKey }) => {
    const [_key, { token, page, isActive, filters, order }] = queryKey;
    return await fetchAllMembers({ token, page, isActive, ...filters, order });
  };

  // Modifique o queryKey para usar o estado 'filters'
  const queryKey = useMemo(
    () => [
      "members",
      {
        token: nodeToken,
        page: membersPage,
        isActive: isOnlyActiveMembers,
        filters,
        order: orderBy,
      },
    ],
    [nodeToken, membersPage, isOnlyActiveMembers, filters, orderBy]
  );

  const {
    data: allMembers = [],
    isLoading: isLoadingMembers,
    refetch: refetchMembers,
    isError: isMembersNotFound,
  } = useQuery(queryKey, fetchMembers, { enabled: !isFirstRender });

  const { data: membersTransactionsHistory } = useQuery(
    ["membersTransactionsHistory", memberDetails.cpf],
    () => fetchTransactionsHistory(nodeToken, memberDetails.cpf),
    { enabled: !!memberDetails.cpf }
  );

  // Conta resultados
  const fetchActiveInativeMembers = async ({ queryKey }) => {
    const [_key, { token, isActive, filters }] = queryKey;
    return await activeInativeMembers({ token, isActive, ...filters });
  };

  // Modifique o queryKey para usar o estado 'filters'
  const queryKeyCount = useMemo(
    () => [
      "countMembers",
      {
        token: nodeToken,
        page: membersPage,
        filters,
        order: orderBy,
      },
    ],
    [nodeToken, membersPage, filters, orderBy]
  );

  const {
    data: contAllMembers = [],
    isLoading: isLoadingCountMembers,
    refetch: refetchCountMembers,
  } = useQuery(queryKeyCount, fetchActiveInativeMembers, { enabled: !isFirstRender });

  const mutationActivateMember = useMutation(
    async ({ cpf, action }) => {
      if (action === "active") {
        let activeMember = {
          name: associatedAction.name,
          cpf: associatedAction.cpf,
        };
        associatedAction?.products?.forEach((product) => {
          const { key } = product;
          activeMember[key] = true;
        });
        console.log("🚀 ~ activeMember:", activeMember)
        await setNewMember({ member: activeMember, token: nodeToken });
      } else if (action === "inative") {
        await inativeAssociated({ cpf, token: nodeToken });
      }
    },
    {
      onSuccess: () => {
        refetchMembers();
        refetchCountMembers();
      },
    }
  );

  const handleFilterInputChange = ({ target: { name, value } }) => {
    if (filterTypeInput === "cpf" && name === "cpfInput") setCpfInput(value);
    if (filterTypeInput === "name" && name === "nameInput") setNameInput(value);
    if (filterTypeInput === "address" && name === "addressInput") setAddressInput(value);
    if (filterTypeInput === "addressNum" && name === "addressNumInput") setAddressNumInput(value);
    if (filterTypeInput === "registrationDate" && name === "initialDate") setInitialDate(value);
    if (filterTypeInput === "registrationDate" && name === "finalDate") setFinalDate(value);
  };

  const handleResetFilterButton = () => {
    setCpfInput("");
    setNameInput("");
    setAddressInput("");
    setAddressNumInput("");
    setProductType("");
    setInitialDate("");
    setFinalDate("");
    setFilters({});
  };

  // Modifique a função submitFilter para atualizar o estado 'filters'
  const submitFilter = (event) => {
    event.preventDefault();
    setMembersPage(0);
    setFilters({
      cpf: cpfInput,
      name: nameInput,
      address: addressInput,
      addressNum: addressNumInput,
      product: productType,
      initialDate,
      finalDate,
    });
  };

  const handleOrderChange = (e) => {
    const newOrderBy = orderBy === "name" ? "" : "name";
    setOrderBy(newOrderBy);
    localStorage.setItem("orderBy", newOrderBy);
    console.log(newOrderBy);
  };

  const handleMemberActivationDialog = (
    { currentTarget: { name: cpf } },
    type
  ) => {
    setAssociatedAction(type);
    if (!!cpf) setMemberToBeActivatedCpf(cpf);
    setIsMemberActivationDialogOpen(!isMemberActivationDialogOpen);
  };

  const handleActivateMember = async () => {
    setIsMemberActivationDialogOpen(!isMemberActivationDialogOpen);
    setIsMemberDrawerOpen(false);
    setMemberDetails("");
    mutationActivateMember.mutate({
      cpf: memberToBeActivatedCpf,
      action: associatedAction.type,
    });
  };

  const handleCloseDrawer = () => {
    setIsMemberDrawerOpen(false);
    setMemberDetails("");
  };

  useEffect(() => {
    setIsFirstRender(false);
    setIsMemberDrawerOpen(false);
  }, [orderBy]);

  useEffect(() => {
    if (!isFirstRender) {
      setIsMemberDrawerOpen(false);
    }
  }, [membersPage, isOnlyActiveMembers]);

  const context = {
    filterTypeInput,
    setFilterTypeInput,
    handleFilterInputChange,
    cpfInput,
    nameInput,
    addressInput,
    addressNumInput,
    setProductType,
    productType,
    submitFilter,
    handleResetFilterButton,
    initialDate,
    finalDate,
  };

  const formatData = (data) => {
    const formattedDate = data.split("T")[0];
    const [year, month, day] = formattedDate.split("-");
    const newFormatDate = `${day}-${month}-${year}`;
    return newFormatDate;
  };

  return (
    <MembersContext.Provider value={context}>
      <Dialog
        open={isMemberActivationDialogOpen}
        onClose={handleMemberActivationDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {mutationActivateMember.isLoading
            ? "Aguarde..."
            : "Tem certeza dessa ação?"}
        </DialogTitle>
        <DialogActions sx={{ display: "flex", justifyContent: "center" }}>
          {mutationActivateMember.isLoading ? (
            <CircularProgress />
          ) : (
            <>
              <Button color="error" onClick={handleMemberActivationDialog}>
                Cancelar
              </Button>
              <Button onClick={handleActivateMember}>Concordar</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          overflowX: "auto",
          maxWidth: "100%",
        }}
      >
        <Stack spacing={3} mb={3} sx={{ width: "100%" }}>
          <Container className="mb-6">
            <Grid
              container
              spacing={2}
              sx={{
                justifyContent: { xs: "center", md: "space-between" },
                alignItems: "flex-start",
              }}
            >
              <Grid item xs={12} md={4}>
                <Stack spacing={2}>
                  <CustomButton
                    color="primary"
                    onClick={() => navigation("/new-member")}
                    sx={{
                      alignItems: "center",
                      display: "flex",
                      width: { xs: "100%", sm: "auto" },
                    }}
                    type="button"
                    variant="contained"
                  >
                    <Typography sx={{ lineHeight: 1.5 }} variant="button">
                      Cadastrar Associado
                    </Typography>
                    <CreateIcon sx={{ ml: 1 }} />
                  </CustomButton>
                  <MembersFilter />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4}>
                <ActiveMembersCard
                  isMemberDrawerOpen={isMemberDrawerOpen}
                  isSelected={isOnlyActiveMembers}
                  getMembers={() => {
                    if (isOnlyActiveMembers === false) {
                      setMembersPage(0);
                      setIsOnlyActiveMembers(true);
                    }
                  }}
                  activeMembers={
                    !isLoadingCountMembers
                      ? contAllMembers?.activeUsers
                      : <div className="animate-pulse bg-gray-300 h-6 rounded-md w-32" size={30} />
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InactiveMembersCard
                  isMemberDrawerOpen={isMemberDrawerOpen}
                  isSelected={isOnlyActiveMembers}
                  getMembers={() => {
                    if (isOnlyActiveMembers === true) {
                      setMembersPage(0);
                      setIsOnlyActiveMembers(false);
                    }
                  }}
                  inativeMembers={
                    !isLoadingCountMembers
                      ? contAllMembers?.inactiveUsers
                      : <div className="animate-pulse bg-gray-300 h-6 rounded-md w-32" size={30} />
                  }
                />
              </Grid>
            </Grid>
          </Container>
          <CustomContainer
          >
            <Box sx={{ overflowX: "auto", width: "100%" }}>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left">
                      {isOnlyActiveMembers ? "Inativar" : "Reativar"}
                    </TableCell>
                    <TableCell align="left" sx={{ minWidth: { xs: "80px", sm: "110px" } }}>
                      Cadastro
                    </TableCell>
                    <TableCell align="left" sx={{ minWidth: { xs: "120px", sm: "140px" } }}>
                      CPF
                    </TableCell>
                    <TableCell align="left" sx={{ minWidth: { xs: "200px", sm: "260px" } }}>
                      <div className="flex w-full items-center justify-left">
                        <FaArrowUp
                          className={`${orderBy === "name" ? "text-green-600" : "text-gray-300"}`}
                          onClick={handleOrderChange}
                          style={{ cursor: "pointer", color: "primary", marginRight: "2px" }}
                          size={12}
                        />
                        Nome
                      </div>
                    </TableCell>
                    { hasTelemedicine &&
                      <TableCell align="left" sx={{ minWidth: { xs: "40px", sm: "80px" } }}>
                        <div title="Telemedicina" className="flex gap-1 items-center">
                          <p>Link</p>
                          <FaPlusSquare className="text-blue-500"/>
                        </div>
                      </TableCell>
                    }
                    <TableCell align="left" sx={{ minWidth: { xs: "200px", sm: "400px" } }}>
                      Produtos
                    </TableCell>
                    <TableCell align="left">Editar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allMembers?.length && !isLoadingMembers ? (
                    allMembers?.map(
                      (
                        {
                          id,
                          cpf,
                          name,
                          email,
                          phone,
                          telemedicinePlan,
                          birthDate,
                          gender,
                          createdAt,
                          active,
                          products,
                          address,
                          addressNum,
                        },
                        index
                      ) => (
                        <TableRow
                          key={`${id}-${cpf}`}
                          className={`${
                            (mutationActivateMember.isLoading && cpf === memberToBeActivatedCpf) ||
                            (isLoadingMembers && cpf === memberToBeActivatedCpf)
                              ? "animate-pulse bg-gray-300"
                              : ""
                          } ${index % 2 === 0 ? "" : "bg-gray-100"}`}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="left">
                            {active ? (
                              <IconButton
                                name={cpf}
                                onClick={(e) =>
                                  handleMemberActivationDialog(e, {
                                    type: "inative",
                                    id,
                                    name,
                                    cpf,
                                    products,
                                  })
                                }
                                sx={{ mx: 1, p: 0 }}
                              >
                                <RemoveCircleIcon color="error" />
                              </IconButton>
                            ) : (
                              <IconButton
                                name={cpf}
                                onClick={(e) =>
                                  handleMemberActivationDialog(e, {
                                    type: "active",
                                    id,
                                    name,
                                    cpf,
                                    products,
                                  })
                                }
                                sx={{ mx: 1, p: 0 }}
                              >
                                <AddCircleIcon color="primary" />
                              </IconButton>
                            )}
                          </TableCell>
                          <TableCell align="left">
                            {createdAt ? formatData(createdAt) : "-"}
                          </TableCell>
                          <TableCell align="left">{cpf ? cpf : "-"}</TableCell>
                          <TableCell align="left">
                            {name ? name.toUpperCase() : "-"}
                          </TableCell>
                          { hasTelemedicine &&
                            <TableCell align="left">
                                {
                                  products?.length ? (
                                    products.map((el, index) => (
                                      Number(el.id) === 4 || Number(el.id) === 14 ? (
                                        <FaLaptopMedical
                                        title="Pegar Links de Consulta e Emergência"
                                          className="h-5 w-5 duration-150 ease-in-out text-blue-300 hover:text-blue-500 cursor-pointer"
                                          onClick={() => {
                                            setSelecteduserCpf(cpf);
                                            setOpenModal(true);
                                          }}
                                        />
                                      ) : null)
                                    )
                                  ) : (
                                    <span>-</span>
                                  )
                                }
                            </TableCell>
                          }
                          <TableCell align="left">
                            <div className="flex flex-wrap gap-1">
                              {products?.length ? (
                                products.map((el, index) => (
                                  <span
                                    key={index}
                                    className="bg-gray-300 font-medium rounded-md px-2 py-1"
                                  >
                                    {el.name.toLowerCase()}
                                  </span>
                                ))
                              ) : (
                                <span>-</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell align="left">
                            <IconButton
                              name={cpf}
                              onClick={(e) => {
                                setEditMember({
                                  id,
                                  name,
                                  cpf,
                                  products,
                                  email,
                                  gender,
                                  phone,
                                  telemedicinePlan,
                                  birthDate,
                                  address,
                                  addressNum,
                                });
                                navigation("/edit-member");
                              }}
                              sx={{ mx: 1, p: 0 }}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )
                    )
                  ) : (
                    <></>
                  )}
                </TableBody>
              </Table>
            </Box>
            {isLoadingMembers && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                  width: "100%",
                }}
              >
                <CircularProgress />
              </Box>
            )}

            {isMembersNotFound && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  py: 2,
                  width: "100%",
                }}
              >
                <Typography component="p" mt={3} variant="h4">
                  Associado não encontrado
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 4,
                maxWidth: "100%",
              }}
            >
              <ButtonGroup size="small">
                <CustomButton
                  disabled={isLoadingMembers}
                  onClick={() => {
                    setMembersPage(0);
                  }}
                  variant="contained"
                >
                  <FirstPageRoundedIcon />
                </CustomButton>
                <CustomButton
                  disabled={isLoadingMembers}
                  onClick={() => {
                    if (Number(membersPage) > 0) {
                      setMembersPage((prev) => (Number(prev) - 1).toString());
                    }
                  }}
                  variant="contained"
                >
                  <NavigateBeforeRoundedIcon />
                </CustomButton>
                <CustomButton
                  disabled={isLoadingMembers}
                  onClick={() => {
                    setCpfInput("");
                    setMembersPage((prev) => Number(prev) + 1);
                  }}
                  variant="contained"
                >
                  <NavigateNextRoundedIcon />
                </CustomButton>
              </ButtonGroup>
              <Box
                sx={{
                  alignItems: "center",
                  backgroundColor: "white",
                  borderRadius: "10px",
                  boxShadow: "0px 0px 15px 0px rgb(88 88 88 / 30%)",
                  display: "flex",
                  justifyContent: "center",
                  ml: 1,
                  width: "40px",
                }}
              >
                <Typography color="primary" component="p" variant="h6">
                  {membersPage}
                </Typography>
              </Box>
            </Box>
          </CustomContainer>
        </Stack>
        { openModal &&
          <ModalTelemedicine
            token={nodeToken}
            cpf={selectedUserCpf}
            onClose={closeModal}
          />
        }
      </Box>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="temporary" // Alterado para 'temporary'
        anchor="right"
        open={!!isMemberDrawerOpen}
        onClose={handleCloseDrawer} // Adicionado para permitir fechar o Drawer
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "space-between",
            pt: 1,
          }}
        >
          <IconButton onClick={handleCloseDrawer} sx={{ m: 1 }}>
            {theme.direction === "rtl" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
          <Box
            sx={{
              alignItems: "flex-end",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {!!memberDetails && (
              <>
                <Typography
                  color="primary"
                  component="h4"
                  fontWeight={600}
                  px={1}
                  variant="h5"
                >
                  {`${memberDetails.nome}`}
                </Typography>
                <Typography
                  color="primary"
                  component="h2"
                  px={1}
                  variant={!!memberDetails.nome ? "subtitle1" : "h5"}
                >
                  {memberDetails.cpf}
                </Typography>
              </>
            )}
          </Box>
        </Box>
        {
          <Stack px={2} py={1} spacing={2}>
            {!!memberDetails.numeroDeUtilizacoes &&
              !!memberDetails.economia && (
                <Stack direction="row" spacing={2}>
                  <MemberNumberOfUsesCard
                    numberOfUses={memberDetails.numeroDeUtilizacoes}
                  />
                  <MemberGeneratedEconomyCard
                    economy={memberDetails.economia}
                  />
                </Stack>
              )}
            <Stack direction="row" spacing={2}>
              {!!memberDetails.ativo && !!memberDetails.cpf && (
                <CustomContainer
                  sx={{
                    alignItems: "center",
                    borderRadius: "30px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography>
                    {memberDetails.ativo === "Ativo"
                      ? "Associado ativo"
                      : "Associado inativo"}
                  </Typography>
                  <Switch
                    checked={memberDetails.ativo === "Ativo"}
                    color="primary"
                    name={memberDetails.cpf}
                    onChange={(event) => {
                      handleMemberActivationDialog(event);
                    }}
                  />
                </CustomContainer>
              )}
              <CustomContainer
                sx={{
                  alignItems: "center",
                  borderRadius: "30px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  textAlign: "center",
                }}
              >
                <Typography>Praça principal:</Typography>
                <Typography color="primary">
                  {memberDetails.pracaPrincipal || "-"}
                </Typography>
              </CustomContainer>
            </Stack>

            <CustomContainer>
              <Typography color="primary" component="p" pl={2} variant="h6">
                Histórico de consultas
              </Typography>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Data - Hora</TableCell>
                    <TableCell align="center">Tipo de Consulta</TableCell>
                    <TableCell align="center">Estabelecimento</TableCell>
                    <TableCell align="center">Termo Buscado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!!membersTransactionsHistory &&
                    membersTransactionsHistory.map(
                      ({ Data, Pagina, Estabelecimento, Busca }, index) => (
                        <TableRow
                          key={Data.split("").reverse().join() + index}
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell align="center" component="th" scope="row">
                            {`${dateTimeFormatter(Data).split(" - ")[0]} \n ${
                              dateTimeFormatter(Data).split(" - ")[1]
                            }`}
                          </TableCell>
                          <TableCell align="center">
                            {Pagina.charAt(0).toUpperCase() + Pagina.slice(1)}
                          </TableCell>
                          <TableCell align="center">
                            {!!Number(Estabelecimento) ? Estabelecimento : "-"}
                          </TableCell>
                          {Pagina === "login" || Pagina === "descontos" ? (
                            <TableCell align="center">{"-"}</TableCell>
                          ) : Pagina === "busca" ? (
                            <TableCell align="center">
                              <Box
                                sx={{
                                  borderBottom: "1px solid rgb(88 88 88 / 30%)",
                                  px: 1,
                                  py: 0.5,
                                }}
                              >
                                {Busca}
                              </Box>
                            </TableCell>
                          ) : (
                            <TableCell align="center">
                              <Box
                                sx={{
                                  borderRadius: "5px",
                                  boxShadow:
                                    "0 0 10px 0 rgb(88 88 88 / 30%) inset",
                                  px: 1,
                                  py: 0.5,
                                }}
                              >
                                {Busca}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      )
                    )}
                </TableBody>
              </Table>
              {!membersTransactionsHistory && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                    width: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </CustomContainer>
          </Stack>
        }
      </Drawer>
    </MembersContext.Provider>
  );
}
